import { useState, useEffect, useCallback, useMemo } from "react";
import AdminLayout from "@/components/AdminLayout";
import api, { formatApiError } from "@/utils/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Users, Clock, Layers, Pencil, Trash2, Plus, Shield } from "lucide-react";

export default function AdminGroups() {
  const [groups, setGroups] = useState([]);
  const [courses, setCourses] = useState([]);
  const [modules, setModules] = useState([]);
  const [studentCounts, setStudentCounts] = useState({});
  const [instructors, setInstructors] = useState([]);
  const [editGroup, setEditGroup] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState({ name: "", schedule: "", current_module_id: "", status: "starting_soon" });

  const load = useCallback(async () => {
    const [g, c, m, u] = await Promise.all([
      api.get("/groups"), api.get("/courses"), api.get("/modules"),
      api.get("/users", { params: { role: "instructor" } })
    ]);
    setGroups(g.data);
    setCourses(c.data);
    setModules(m.data);
    setInstructors(u.data);
    const students = await api.get("/students");
    const counts = {};
    students.data.forEach((s) => { if (s.group_id) counts[s.group_id] = (counts[s.group_id] || 0) + 1; });
    setStudentCounts(counts);
  }, []);

  useEffect(() => { load(); }, [load]);

  const openEdit = (g) => {
    setEditGroup(g);
    setForm({
      name: g.name, schedule: g.schedule || "",
      current_module_id: g.current_module_id || "none",
      status: g.status || "starting_soon"
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    try {
      const updates = { ...form };
      if (updates.current_module_id === "none") updates.current_module_id = null;
      await api.put(`/groups/${editGroup.id}`, updates);
      toast.success("Group updated");
      setDialogOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const getModuleName = (id) => modules.find((m) => m.id === id)?.title || "None";
  const getCourseName = (id) => courses.find((c) => c.id === id)?.title || "Unknown";

  const statusLabels = { starting_soon: "Starting Soon", in_progress: "In Progress", completed: "Completed" };

  const filteredModulesForEdit = useMemo(() => {
    if (!editGroup) return modules;
    return modules.filter((m) => m.course_id === editGroup.course_id);
  }, [modules, editGroup]);

  const handleDeleteGroup = async (g) => {
    if (!window.confirm(`Delete group "${g.name}"? Students in this group will be unassigned.`)) return;
    try {
      await api.delete(`/groups/${g.id}`);
      toast.success("Group deleted");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const [createOpen, setCreateOpen] = useState(false);
  const [createForm, setCreateForm] = useState({ name: "", schedule: "", course_id: "", status: "starting_soon" });

  const openCreate = () => {
    setCreateForm({ name: "", schedule: "", course_id: courses[0]?.id || "", status: "starting_soon" });
    setCreateOpen(true);
  };

  const handleCreate = async () => {
    try {
      await api.post("/groups", createForm);
      toast.success("Group created");
      setCreateOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <AdminLayout>
      <div data-testid="admin-groups" className="space-y-6 animate-fade-in-up">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="font-heading text-3xl sm:text-4xl text-[#5C3A2E] font-semibold tracking-tight">Groups</h1>
            <p className="text-[#2B2B2B]/50 mt-1 text-sm">Manage time slots and group progression</p>
          </div>
          <Button data-testid="create-group-button" onClick={openCreate} className="bg-[#5C3A2E] text-white rounded-full hover:bg-[#5C3A2E]/90">
            <Plus className="w-4 h-4 mr-1.5" /> New Group
          </Button>
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          {groups.map((g) => (
            <div key={g.id} data-testid={`group-card-${g.id}`} className="bg-white rounded-xl shadow-[0_8px_30px_rgb(92,58,46,0.08)] border border-[#EADBC8]/30 p-6 studio-card">
              <div className="flex items-start justify-between mb-3">
                <div>
                  <h3 className="font-heading text-lg text-[#5C3A2E] font-semibold">{g.name}</h3>
                  <p className="text-xs text-[#2B2B2B]/40 mt-0.5">{getCourseName(g.course_id)}</p>
                </div>
                <Button data-testid={`edit-group-${g.id}`} variant="ghost" size="sm" onClick={() => openEdit(g)} className="text-[#5C3A2E] hover:bg-[#EADBC8]/30 rounded-full h-8 w-8 p-0">
                  <Pencil className="w-3.5 h-3.5" />
                </Button>
                <Button data-testid={`delete-group-${g.id}`} variant="ghost" size="sm" onClick={() => handleDeleteGroup(g)} className="text-red-400 hover:bg-red-50 rounded-full h-8 w-8 p-0">
                  <Trash2 className="w-3.5 h-3.5" />
                </Button>
              </div>
              <div className="space-y-2 text-sm">
                <div className="flex items-center gap-2 text-[#2B2B2B]/60">
                  <Clock className="w-3.5 h-3.5" />
                  <span>{g.schedule || "No schedule set"}</span>
                </div>
                <div className="flex items-center gap-2 text-[#2B2B2B]/60">
                  <Layers className="w-3.5 h-3.5" />
                  <span>Module: {g.current_module_id ? getModuleName(g.current_module_id) : "None assigned"}</span>
                </div>
                <div className="flex items-center gap-2 text-[#2B2B2B]/60">
                  <Users className="w-3.5 h-3.5" />
                  <span>{studentCounts[g.id] || 0} {(studentCounts[g.id] || 0) === 1 ? "student" : "students"}</span>
                </div>
                {(() => {
                  const gi = instructors.filter((i) => (i.group_ids || []).includes(g.id) || i.group_id === g.id);
                  return gi.length > 0 ? (
                    <div className="flex items-center gap-2 text-[#C6A969]">
                      <Shield className="w-3.5 h-3.5" />
                      <span>{gi.map((i) => i.name).join(", ")}</span>
                    </div>
                  ) : null;
                })()}
              </div>
              <div className="mt-3">
                <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium uppercase tracking-wider status-${g.status || "starting_soon"}`}>
                  {statusLabels[g.status] || "Starting Soon"}
                </span>
              </div>
            </div>
          ))}
        </div>

        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="bg-white rounded-xl max-w-md">
            <DialogHeader>
              <DialogTitle className="font-heading text-xl text-[#5C3A2E]">Edit Group</DialogTitle>
              <DialogDescription className="text-[#2B2B2B]/50 text-sm">Update group settings and progression</DialogDescription>
            </DialogHeader>
            <div className="space-y-4 mt-2">
              <div>
                <label className="text-sm font-medium text-[#2B2B2B] mb-1 block">Name</label>
                <Input data-testid="group-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="rounded-xl border-[#EADBC8]" />
              </div>
              <div>
                <label className="text-sm font-medium text-[#2B2B2B] mb-1 block">Schedule</label>
                <Input data-testid="group-schedule-input" value={form.schedule} onChange={(e) => setForm({ ...form, schedule: e.target.value })} className="rounded-xl border-[#EADBC8]" />
              </div>
              <div>
                <label className="text-sm font-medium text-[#2B2B2B] mb-1 block">Current Module</label>
                <Select data-testid="group-module-select" value={form.current_module_id} onValueChange={(v) => setForm({ ...form, current_module_id: v })}>
                  <SelectTrigger className="rounded-xl border-[#EADBC8]">
                    <SelectValue placeholder="Select module" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None</SelectItem>
                    {filteredModulesForEdit.map((m) => (
                      <SelectItem key={m.id} value={m.id}>{m.title}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm font-medium text-[#2B2B2B] mb-1 block">Status</label>
                <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                  <SelectTrigger data-testid="group-status-select" className="rounded-xl border-[#EADBC8]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="starting_soon">Starting Soon</SelectItem>
                    <SelectItem value="in_progress">In Progress</SelectItem>
                    <SelectItem value="completed">Completed</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <Button data-testid="save-group-button" onClick={handleSave} className="w-full bg-[#5C3A2E] text-white rounded-full hover:bg-[#5C3A2E]/90 h-11">
                Save Changes
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Create Group Dialog */}
        <Dialog open={createOpen} onOpenChange={setCreateOpen}>
          <DialogContent className="bg-white rounded-xl max-w-md">
            <DialogHeader>
              <DialogTitle className="font-heading text-xl text-[#5C3A2E]">New Group</DialogTitle>
              <DialogDescription className="text-[#2B2B2B]/50 text-sm">Create a new time slot group</DialogDescription>
            </DialogHeader>
            <div className="space-y-4 mt-2">
              <div>
                <label className="text-sm font-medium text-[#2B2B2B] mb-1 block">Name</label>
                <Input data-testid="create-group-name" value={createForm.name} onChange={(e) => setCreateForm({ ...createForm, name: e.target.value })} placeholder="e.g., Wednesdays 5pm–7pm" className="rounded-xl border-[#EADBC8]" />
              </div>
              <div>
                <label className="text-sm font-medium text-[#2B2B2B] mb-1 block">Schedule</label>
                <Input value={createForm.schedule} onChange={(e) => setCreateForm({ ...createForm, schedule: e.target.value })} placeholder="e.g., Wednesdays 5:00 PM - 7:00 PM" className="rounded-xl border-[#EADBC8]" />
              </div>
              <div>
                <label className="text-sm font-medium text-[#2B2B2B] mb-1 block">Course</label>
                <Select value={createForm.course_id} onValueChange={(v) => setCreateForm({ ...createForm, course_id: v })}>
                  <SelectTrigger className="rounded-xl border-[#EADBC8]"><SelectValue placeholder="Select course" /></SelectTrigger>
                  <SelectContent>
                    {courses.map((c) => <SelectItem key={c.id} value={c.id}>{c.title}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <Button data-testid="save-new-group" onClick={handleCreate} className="w-full bg-[#5C3A2E] text-white rounded-full hover:bg-[#5C3A2E]/90 h-11">
                Create Group
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
}
